/**
 * footnoteActionExecutors — les TROIS verbes de NOTE DE BAS DE PAGE du canvas.
 *
 * > Une note de bas de page est le geste de l'avocat qui SOURCE : poser la
 * > référence d'un arrêt, l'article visé, l'incise qui casserait le
 * > raisonnement si elle restait dans le corps. L'agent savait écrire, mettre
 * > en forme et annoter ; il ne savait pas renvoyer. `insert_footnote`,
 * > `edit_footnote`, `delete_footnote`.
 *
 * L'analogue structurel est `executeAddComments` (#996) : une entité ANCRÉE sur
 * du texte, dont l'ancre se dérive par `query.match` et dont l'identifiant est
 * rendu par la création. Les sept temps sont les mêmes — résoudre la cible,
 * instantané **pre**, gardes de capacité, dériver l'ancre, **un seul** appel
 * mutant sous `muterSousFences`, instantané **post** + `verifierEpoque`,
 * receipt dont la postcondition est une RELECTURE.
 *
 * ## Les formes d'appel sont MESURÉES (relevé du 2026-08-26)
 *
 * `tests/ws-mocked/mesure-footnotes.spec.ts`, §9 de
 * `docs/features/redaction-web-canvas/superdoc-document-api.md`. Aucune n'était
 * lisible dans un typage :
 *
 * - **Poser** : `footnotes.insert({at, type, content})` où `at` est un
 *   `{kind:'text', segments:[{blockId, range:{start, end}}]}` **explicite**.
 *   Le typage annonce `at` optionnel (« host runtimes insert at the current
 *   selection ») et l'adaptateur v2 rend en réalité `PRECONDITION_FAILED —
 *   requires an explicit target address`. Le texte de la note voyage sous
 *   `content`, jamais sous `text`.
 * - **Réécrire** : `footnotes.update({target, patch:{content}})`, où `target`
 *   est l'adresse d'ENTITÉ `{kind:'entity', entityType:'footnote', noteId}` —
 *   pas un `noteId` à plat.
 * - **Retirer** : `footnotes.remove({target})`, même adresse d'entité.
 * - **Relire** : `footnotes.list()` rend l'enveloppe de découverte
 *   `{evaluatedRevision, total, items, page}` — comme `trackChanges.list` —
 *   chaque item portant `{id, handle, address, type, noteId, displayNumber,
 *   content}`.
 *
 * ## ⚠️ Deux pièges mesurés, tous deux portés par ce module
 *
 * 1. **Un bloc portant une RÉVISION SUIVIE PENDANTE est fermé à toute note** :
 *    `CAPABILITY_UNAVAILABLE — « footnotes.insert currently supports plain
 *    paragraph text runs only »`. Établi de bout en bout le 2026-08-26
 *    (`tests/ws-mocked/canvas-notes-de-bas-de-page.spec.ts`) en croisant régime
 *    × état du bloc : un bloc vierge passe, un bloc portant une note posée en
 *    DIRECT passe, un bloc simplement réécrit par `rewrite_block` suivi est
 *    refusé — **même sans aucune note** — et repasse une fois la révision
 *    acceptée. Le régime du second appel n'y change rien.
 *
 *    ⚠️ Ce n'est donc NI « une note par paragraphe » (plusieurs y cohabitent,
 *    y compris adjacentes) NI un défaut d'ancre : changer d'ancre dans le même
 *    bloc échoue autant. Deux lectures fausses de ce même message se sont déjà
 *    succédé dans ce module — d'où le libellé fournisseur recopié TEL QUEL, et
 *    un `nextAction` qui envoie vers `accept_tracked_changes` plutôt que vers
 *    une autre ancre. En production le régime suivi est le DÉFAUT : l'agent ne
 *    peut pas sourcer un bloc qu'il vient de réécrire dans le même tour.
 * 2. **Une suppression suivie ne retire PAS la note de `footnotes.list()`** :
 *    elle y reste, avec son contenu, marquée en attente de revue. Aucune
 *    postcondition de ce module ne prouve donc une suppression par COMPTAGE —
 *    voir `controleDeSuppression`.
 *
 * ## Le régime de révision, verbe par verbe
 *
 * Sous fenêtre `suggesting`, témoin `doc.replace` à 1 révision : `insert` et
 * `remove` en produisent **1** chacun, `update` **ZÉRO** malgré son
 * `tracked: true` déclaré — quatrième démenti du `tracked` annoncé, et
 * *contrôlé* sur une note commitée, pas seulement sur une insertion pendante.
 * D'où `reviewPolicy:'direct'` au catalogue pour `edit_footnote` et le
 * `changeMode:'direct'` écrit EN DUR ici.
 */

import type { CanvasActionInput } from '@hector/shared-types';
import {
  abortedBeforeMutation,
  buildVerifiedReceipt,
  toCanvasSnapshotSummary,
  type CanvasActionReceipt,
  type VerificationCheck,
} from './actionReceipt';
import {
  idsDeRevision,
  type CanvasSnapshot,
  type CanvasSnapshotBlock,
  type CanvasSnapshotFootnote,
} from './canvasSnapshot';
import { estUnRefus, resoudreLaCible } from './formatActionExecutors';
import { muterSousFences, revisionCourante } from './fencesDeMutation';
import { chaine, entrees, isRecord, refusDeCapacite as refusDeCapaciteDeLEditeur, refusDuFournisseur } from './reponsesDeLEditeur';
import { modeDeRevision, snapshot, targetSummary, type CanvasActionContext } from './textActionExecutors';

export type InsertFootnoteInput = Extract<CanvasActionInput, { action: 'insert_footnote' }>;
export type EditFootnoteInput = Extract<CanvasActionInput, { action: 'edit_footnote' }>;
export type DeleteFootnoteInput = Extract<CanvasActionInput, { action: 'delete_footnote' }>;

type NomDeVerbeDeNote = InsertFootnoteInput['action'] | EditFootnoteInput['action'] | DeleteFootnoteInput['action'];

/** Borne d'extrait pour les messages rendus au modèle — ils partent dans sa fenêtre de contexte. */
const MAX_EXTRAIT = 80;

function extrait(texte: string): string {
  const compact = texte.replace(/\s+/gu, ' ').trim();
  return compact.length > MAX_EXTRAIT ? `${compact.slice(0, MAX_EXTRAIT)}…` : compact;
}

/**
 * Le texte relu correspond-il à celui qu'on a demandé d'écrire ?
 *
 * ⚠️ **Une égalité stricte serait FAUSSE, et le défaut serait silencieux.** La
 * lecture partagée (`canvasSnapshot.lireLesNotes`) rogne le texte d'une note :
 * elle le `trim()` et le BORNE à 999 caractères en ajoutant une ellipse, parce
 * que le contrat d'inspection est `.strict()` et qu'une note d'une page ferait
 * sinon rejeter le receipt ENTIER. Or `edit_footnote.text` accepte jusqu'à
 * 2 000 caractères : comparer à l'identique déclarerait en échec une réécriture
 * pourtant appliquée, dès qu'une note de doctrine dépasse la borne.
 *
 * On compare donc sur ce que la borne a laissé passer : égalité après `trim`,
 * ou préfixe suivi de l'ellipse.
 */
function correspondAuTexteDemande(relu: string | undefined, demande: string): boolean {
  const attendu = demande.trim();
  if (relu === undefined) return attendu.length === 0;
  return relu === attendu || (relu.endsWith('…') && attendu.startsWith(relu.slice(0, -1)));
}

/** L'adresse d'ENTITÉ qu'exigent `footnotes.update` et `footnotes.remove` (mesurée). */
function adresseDeNote(noteId: string): { kind: 'entity'; entityType: 'footnote'; noteId: string } {
  return { kind: 'entity', entityType: 'footnote', noteId };
}

/** L'identifiant rendu par l'insertion — forme mesurée `{success:true, footnote:{…, noteId}}`. */
function noteIdCree(resultat: unknown): string | undefined {
  if (!isRecord(resultat)) return undefined;
  const footnote = resultat.footnote;
  return (isRecord(footnote) ? chaine(footnote.noteId) : undefined) ?? chaine(resultat.noteId);
}

/**
 * Le refus du fournisseur, RENDU TEL QUEL — et c'est le point.
 *
 * ⚠️ `aborted`, pas `failed` : le fournisseur a refusé l'appel, donc **rien
 * n'a été écrit** et le document est intact. `failed` enverrait l'agent
 * réinspecter un document que personne n'a touché ; `aborted` dit « change de
 * cible ou de verbe », qui est la seule sortie.
 *
 * Le message du fournisseur est recopié entre guillemets sans être reformulé :
 * il nomme une contrainte qu'aucune forme d'entrée n'exprime (« currently
 * supports plain paragraph text runs only »), et la paraphraser ferait perdre
 * le seul mot qui permet à un lecteur de log de la retrouver. Le fournisseur
 * est ici mieux placé que nous : on a déjà attribué ce message à une mauvaise
 * cause une fois (2026-08-26), et seul son libellé exact a permis de revenir
 * sur l'erreur.
 */
function receiptDeRefusFournisseur(input: {
  intent: NomDeVerbeDeNote;
  resume: CanvasSnapshot;
  resultat: unknown;
  sortie: string;
  nextAction: string;
  recovery: NonNullable<CanvasActionReceipt['recovery']>;
}): CanvasActionReceipt {
  const refus = refusDuFournisseur(input.resultat);
  return abortedBeforeMutation({
    intent: input.intent,
    snapshot: toCanvasSnapshotSummary(input.resume),
    code: refus.code,
    message: `${input.intent} a été REFUSÉ par l'éditeur et RIEN n'a été écrit. Refus rendu tel quel : `
      + `« ${refus.message} ». ${input.sortie}`,
    nextAction: input.nextAction,
    recovery: input.recovery,
  });
}

/** Le refus de capacité des trois verbes de note (`reponsesDeLEditeur.ts`). */
function refusDeCapacite(intent: NomDeVerbeDeNote, resume: CanvasSnapshot, surface: string, contournement: string): CanvasActionReceipt {
  return refusDeCapaciteDeLEditeur({ intent, resume, surface, contournement });
}

/**
 * Le refus NOMMÉ d'un `note_id` que le document ne porte pas.
 *
 * Même rédaction que `reply_to_comment` sur un `commentId` inconnu (#996), et
 * pour la même raison : un identifiant ne se devine ni ne se compose, et le
 * modèle lit ce refus hors contexte — il doit donc y trouver le domaine
 * d'inspection à demander, pas seulement le constat d'échec.
 */
function refusDeNoteInconnue(
  intent: NomDeVerbeDeNote,
  resume: CanvasSnapshot,
  noteId: string,
  notes: readonly CanvasSnapshotFootnote[],
): CanvasActionReceipt {
  const connues = notes.slice(0, 8).map((note) => `"${note.noteId}"`).join(', ');
  return abortedBeforeMutation({
    intent,
    snapshot: toCanvasSnapshotSummary(resume),
    code: 'SELECTOR_NOT_FOUND',
    message: `${intent} a été REFUSÉ avant toute écriture : aucune note de bas de page d'identifiant "${noteId}" `
      + `n'existe dans ce document. Il en porte ${notes.length}${notes.length === 0 ? '' : ` (${connues})`}. `
      + 'Rien n\'a été modifié. Un note_id ne se devine ni ne se compose : lance canvas_inspect avec includeDomains '
      + 'contenant "footnotes" et reprends la valeur "noteId" telle quelle.',
  });
}

/**
 * La plage où poser le renvoi : repliée sur la FIN du fragment ancre.
 *
 * ⚠️ **C'est un choix d'ANCRAGE, pas une contrainte du fournisseur.** Le relevé
 * ne dit nulle part où le renvoi atterrit par rapport à la plage passée en
 * `at` ; la sonde de mesure a posé `{start:0, end:0}` sur un paragraphe vierge,
 * ce qui ne discrimine rien. Replier sur la fin du match (`start = end = fin`)
 * place le renvoi **après** le fragment ancre — c'est-à-dire là où un juriste
 * l'écrit : « la prescription est acquise¹ », jamais « la¹ prescription ». Un
 * lecteur qui verrait passer une plage vide sans cette explication croirait à
 * un oubli.
 *
 * La cible est dérivée de la MÊME source que `cibleDeCommentaire` — l'objet
 * `items[0].target` que `query.match` rend lui-même — parce que c'est le seul
 * endroit où vivent, ensemble, le `blockId` et les décalages du match. La forme
 * relue est `{kind:'selection', start:{kind:'text', blockId, offset},
 * end:{offset}}` ; l'adresse de texte veut `{blockId, range:{start, end}}`. La
 * conversion est mécanique, comme celle qu'`add_hyperlink` fait déjà.
 */
async function plageApresLAncre(
  ctx: CanvasActionContext,
  bloc: CanvasSnapshotBlock,
  ancre: string,
): Promise<{ ok: true; segment: { blockId: string; range: { start: number; end: number } } } | { ok: false; code: string; message: string }> {
  const resultat = await ctx.doc.query?.match?.({
    type: 'text',
    pattern: ancre,
    caseSensitive: true,
    require: 'unique',
    within: { kind: 'block', nodeType: bloc.nodeType, nodeId: bloc.nodeId },
  });
  // Seule enveloppe de découverte lue ici : les NOTES sont lues une fois par instantané (`canvasSnapshot`), jamais par un second `footnotes.list`.
  const premier = entrees(resultat)[0];
  const cible = isRecord(premier) ? premier.target : undefined;
  const debut = isRecord(cible) ? cible.start : undefined;
  const fin = isRecord(cible) ? cible.end : undefined;
  const blockId = isRecord(debut) ? chaine(debut.blockId) : undefined;
  const offsetDeFin = isRecord(fin) && typeof fin.offset === 'number' ? fin.offset : undefined;

  if (blockId === undefined || offsetDeFin === undefined) {
    return {
      ok: false,
      code: 'SELECTOR_REF_UNAVAILABLE',
      message: `insert_footnote a été REFUSÉ avant toute écriture : l'appariement de « ${extrait(ancre)} » dans le bloc `
        + `(nodeId=${bloc.nodeId}) n'a rendu aucune plage de texte adressable, et un renvoi ne se pose que sur une `
        + 'plage. Rien n\'a été modifié. Le texte du bloc a changé depuis sa lecture, ou une différence invisible '
        + '(espace insécable, suppression suivie, retour à la ligne) empêche l\'appariement. Relance canvas_inspect '
        + 'pour lire le texte ACTUEL du bloc, puis reprends une ancre qui s\'y trouve littéralement.',
    };
  }
  // Plage VIDE, délibérément : le renvoi se pose au point qui suit l'ancre.
  return { ok: true, segment: { blockId, range: { start: offsetDeFin, end: offsetDeFin } } };
}

/**
 * Poser une note de bas de page (relevé 2026-08-26).
 *
 * ⚠️ Un bloc VIDE n'a aucun texte à ancrer. Le refus porte son propre code : le
 * sélecteur a RÉSOLU (le bloc existe, il est simplement vide), donc
 * `SELECTOR_NOT_FOUND` enverrait chercher une erreur de ciblage qui n'existe
 * pas — même arbitrage que `add_comments`.
 */
export async function executeInsertFootnote(
  ctx: CanvasActionContext,
  input: InsertFootnoteInput,
): Promise<CanvasActionReceipt> {
  const resolue = await resoudreLaCible(ctx, input.selector, 'insert_footnote');
  if (!resolue.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(await snapshot(ctx)),
      code: resolue.code,
      message: resolue.message,
    });
  }
  const cible = resolue.target;
  const pre = await snapshot(ctx);

  if (typeof ctx.doc.footnotes?.insert !== 'function') {
    return refusDeCapacite(input.action, pre, 'footnotes.insert',
      'Il n\'existe aucun contournement fidèle : écrire la référence entre parenthèses dans le corps de l\'acte '
      + 'change le texte que l\'avocat déposera. Dis-lui la référence dans la conversation.');
  }
  if (typeof ctx.doc.query?.match !== 'function') {
    return refusDeCapacite(input.action, pre, 'query.match',
      'C\'est la seule surface qui produise la plage de texte que footnotes.insert accepte en "at" : sans elle, '
      + 'aucun renvoi ne peut être ancré. Dis la référence à l\'avocat dans la conversation.');
  }
  if (cible.fullText.trim().length === 0) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'BLOCK_EMPTY',
      message: `insert_footnote ne peut pas ancrer un renvoi dans un bloc VIDE (nodeId=${cible.nodeId}) : une note `
        + 'de bas de page s\'ancre APRÈS un fragment de TEXTE, et il n\'y en a aucun ici. Rien n\'a été modifié. '
        + 'Vise le bloc qui porte réellement le passage à sourcer.',
      // `reinspect` serait faux : relire rendra le même bloc vide (#991).
      nextAction: 'Vise un bloc qui contient le passage à sourcer.',
      recovery: { action: 'retry', reason: 'Rien n\'a été écrit. Le bloc est vide par nature ; ré-inspecter rendrait le même bloc.' },
    });
  }
  if (!cible.fullText.includes(input.anchor_text)) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: 'SELECTOR_NOT_FOUND',
      message: `insert_footnote a été REFUSÉ avant toute écriture : le bloc résolu (nodeId=${cible.nodeId}) ne contient `
        + `pas l'ancre « ${extrait(input.anchor_text)} », donc le renvoi n'a pas de point d'attache. Rien n'a été `
        + `modifié. Le bloc dit actuellement : « ${extrait(cible.fullText)} ». Reprends comme anchor_text un fragment `
        + 'copié littéralement de ce texte, ou vise un autre bloc.',
    });
  }

  const ancre = await plageApresLAncre(ctx, cible, input.anchor_text);
  if (!ancre.ok) {
    return abortedBeforeMutation({
      intent: input.action,
      snapshot: toCanvasSnapshotSummary(pre),
      code: ancre.code,
      message: ancre.message,
    });
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'insert_footnote:footnotes.insert',
    { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    // ⚠️ `content`, jamais `text` — et `at` est OBLIGATOIRE malgré son typage
    // optionnel : l'adaptateur v2 refuse une insertion sans adresse explicite.
    () => ctx.doc.footnotes?.insert?.(
      { at: { kind: 'text', segments: [ancre.segment] }, type: 'footnote', content: input.text },
      { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    ),
  );

  if (estUnRefus(resultat)) {
    return receiptDeRefusFournisseur({
      intent: input.action,
      resume: pre,
      resultat,
      sortie: 'Cause MESURÉE de ce refus (2026-08-26, bout en bout) : le bloc visé porte une RÉVISION SUIVIE ENCORE '
        + 'PENDANTE, et le fournisseur ferme alors le paragraphe entier à toute note. Ce n\'est ni une limite du '
        + 'nombre de notes par bloc (plusieurs y cohabitent, y compris adjacentes), ni un défaut de ton ancre — '
        + 'changer d\'ancre dans le MÊME bloc échouera autant. Le cas le plus fréquent : tu viens de réécrire ce '
        + 'bloc dans le même tour.',
      nextAction: 'Accepte d\'abord la révision qui couvre ce bloc — accept_tracked_changes, en lisant son id par '
        + 'canvas_inspect domaine trackedChanges — puis rejoue insert_footnote À L\'IDENTIQUE. Si le bloc ne porte '
        + 'aucune révision pendante, alors seulement vise un AUTRE bloc.',
      recovery: {
        action: 'retry',
        reason: 'Rien n\'a été écrit. La même insertion aboutira une fois la révision du bloc acceptée — c\'est le '
          + 'blocage, pas l\'ancre.',
      },
    });
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('insert_footnote:receipt');
  const idNeuf = noteIdCree(resultat);

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [targetSummary(cible, resolue.candidateCount)],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    // `mouvementAttendu` reste au défaut (`apparition`) : `footnotes.insert` est
    // mesuré produisant UNE révision sous fenêtre `suggesting`. En régime
    // direct, `buildVerifiedReceipt` rabat de lui-même sur `aucun`.
    verification: [controleDePose(
      post.footnotes,
      // ⚠️ `footnotes.list` n'est PAS une opération requise d'`insert_footnote`
      // au catalogue : sur un éditeur qui ne sait pas lister ses notes, la pose
      // aboutit quand même. La relecture partagée rendant `[]` aussi bien pour
      // « aucune note » que pour « surface absente », c'est la PRÉSENCE de la
      // surface qui distingue les deux — un `[]` sans surface n'est pas une
      // preuve d'échec.
      typeof ctx.doc.footnotes?.list === 'function',
      idNeuf,
      input.text,
    )],
    effects: [{
      kind: 'insert_footnote',
      // `tracked` : le receipt rabat cette valeur si la relecture ne montre
      // aucune identité neuve (#990) — jamais l'inverse.
      reviewability: 'tracked',
      description: `Note de bas de page posée après « ${extrait(input.anchor_text)} ».`,
      nodeIds: [cible.nodeId],
    }],
  });
}

/**
 * La postcondition de pose : la note neuve doit se RELIRE.
 *
 * L'identifiant rendu par l'insertion est le juge quand il existe ; sinon le
 * contenu, la seule autre chose qu'on connaisse de cette note.
 *
 * ⚠️ `footnotes.list` n'est **pas** une opération requise d'`insert_footnote`
 * au catalogue — l'y déclarer ferait disparaître le verbe sur un document qui
 * ne sait pas lister ses notes, alors que la pose, elle, aboutirait. Une
 * relecture indisponible n'est donc pas un échec : le contrôle retombe sur
 * l'accusé du fournisseur **en le disant**, comme `add_hyperlink` le fait quand
 * `hyperlinks.list` manque.
 */
function controleDePose(
  notesApres: readonly CanvasSnapshotFootnote[],
  relectureDisponible: boolean,
  idNeuf: string | undefined,
  texte: string,
): VerificationCheck {
  if (!relectureDisponible) {
    return {
      name: 'note_relue',
      passed: true,
      message: 'Cet éditeur ne rend pas la liste des notes de bas de page : seule la réponse de l\'éditeur atteste '
        + 'la pose du renvoi, aucune relecture ne la prouve. Ne l\'annonce pas à l\'avocat comme vérifiée.',
    };
  }
  const parId = idNeuf !== undefined && notesApres.some((note) => note.noteId === idNeuf);
  const parTexte = notesApres.some((note) => correspondAuTexteDemande(note.text, texte));
  const trouve = parId || parTexte;
  return {
    name: 'note_relue',
    passed: trouve,
    message: trouve
      ? `Relecture : la note ${idNeuf === undefined ? 'posée' : `(noteId=${idNeuf})`} figure bien dans `
        + `footnotes.list (${notesApres.length} note(s) relue(s)).`
      : 'insert_footnote a été annoncé en succès par l\'éditeur, mais la relecture de footnotes.list ne rend AUCUNE '
        + `note ${idNeuf === undefined ? 'portant ce contenu' : `d'identifiant "${idNeuf}"`} sur les `
        + `${notesApres.length} relue(s). Le renvoi n'est donc pas prouvé posé. Relis le document par canvas_inspect `
        + 'en incluant le domaine "footnotes" avant toute autre action — ne rejoue pas ce verbe à l\'identique, il '
        + 'poserait un doublon si le premier avait bien été enregistré.',
  };
}

/**
 * Réécrire le texte d'une note existante (relevé 2026-08-26).
 *
 * ⚠️ **Ce verbe écrit en DIRECT, quel que soit le régime du document** — et ce
 * n'est pas un arbitrage de confort, c'est la mesure : `footnotes.update` se
 * déclare `tracked: true` et ne produit **AUCUNE** révision suivie, contrôlé
 * sur une note commitée avec le témoin `doc.replace` toujours à 1. Demander le
 * suivi ici mentirait deux fois : au fournisseur, qui l'ignore, et au receipt,
 * que `suiviDemandeNonObtenu` ferait rendre `partial` / `TRACKING_NOT_APPLIED`
 * à **chaque appel réussi** — un succès annoncé en demi-échec.
 *
 * Conséquence pour l'avocat, que la description du verbe porte au modèle :
 * **une note réécrite ne laisse aucune trace relisible en régime suivi.** Il ne
 * verra ni l'ancien texte, ni le nouveau marqué. Quand la formulation compte,
 * la paire `delete_footnote` + `insert_footnote` reste revue de bout en bout,
 * au prix d'un renvoi renuméroté.
 */
export async function executeEditFootnote(
  ctx: CanvasActionContext,
  input: EditFootnoteInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);

  if (typeof ctx.doc.footnotes?.update !== 'function') {
    return refusDeCapacite(input.action, pre, 'footnotes.update',
      'La note garde son texte actuel. Dis à l\'avocat ce qu\'il faudrait y corriger plutôt que de réécrire le '
      + 'passage du corps, qui n\'est pas la note.');
  }
  // `footnotes.list` est une opération REQUISE de ce verbe au catalogue : c'est
  // elle qui prouve que l'identifiant existe avant d'écrire, et elle qui prouve
  // l'effet à la relecture. Son absence est une CAPACITÉ manquante — refuser ici
  // dit à l'agent de changer de verbe, là où un `pre.footnotes` vide le ferait
  // conclure à un mauvais identifiant.
  if (typeof ctx.doc.footnotes.list !== 'function') {
    return refusDeCapacite(input.action, pre, 'footnotes.list',
      'Sans elle, ni l\'existence du note_id ni l\'effet de la réécriture ne peuvent être établis, et une réécriture '
      + 'invérifiable dans un acte est pire qu\'une réécriture non faite.');
  }

  // ⚠️ Le PRÉ-INSTANTANÉ fait foi, exactement comme `pre.comments` pour
  // `reply_to_comment` (#996) : les notes sont lues une fois par instantané,
  // pas une seconde fois ici. Limite héritée et assumée — cette lecture est
  // best-effort, donc un `footnotes.list` en échec rend `[]`, indiscernable
  // d'un document sans note ; le refus dira alors « 0 note », ce qui reste vrai
  // du point de vue de tout ce que l'agent peut savoir du document.
  if (!pre.footnotes.some((note) => note.noteId === input.note_id)) {
    return refusDeNoteInconnue(input.action, pre, input.note_id, pre.footnotes);
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'edit_footnote:footnotes.update',
    // ⚠️ `'direct'` EN DUR, comme les verbes de commentaire — pour un motif
    // MESURÉ, pas par analogie : `footnotes.update` ne produit aucune révision,
    // même éditeur en `suggesting`. Annoncer `tracked` sur un appel qui ne peut
    // structurellement rien produire ferait mentir le code sur son intention.
    { changeMode: 'direct', expectedRevision: attendue },
    () => ctx.doc.footnotes?.update?.(
      { target: adresseDeNote(input.note_id), patch: { content: input.text } },
      { changeMode: 'direct', expectedRevision: attendue },
    ),
  );

  if (estUnRefus(resultat)) {
    return receiptDeRefusFournisseur({
      intent: input.action,
      resume: pre,
      resultat,
      sortie: 'La note garde son texte actuel.',
      nextAction: 'Relis les notes par canvas_inspect avec includeDomains contenant "footnotes", puis reprends le '
        + 'noteId tel quel.',
      recovery: {
        action: 'reinspect',
        reason: 'Rien n\'a été écrit ; l\'état relu des notes dira si l\'identifiant visé existe encore.',
      },
    });
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('edit_footnote:receipt');

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: resultat,
    revisionsAvant,
    revisionsApres: idsDeRevision(post.trackedChanges),
    // ⚠️ MESURÉ : `footnotes.update` ne pose aucune révision, même sous suivi.
    // Exiger une apparition ferait rendre `failed` à TOUTE réécriture réussie.
    // Les identités restent relues : la troisième issue de #939 — une révision
    // disparue sans qu'on l'ait demandé — doit continuer de s'exercer.
    mouvementAttendu: 'aucun',
    verification: [controleDeReecriture(post.footnotes, input.note_id, input.text)],
    effects: [{
      kind: 'edit_footnote',
      // Jamais `tracked` : le rabattement de `buildVerifiedReceipt` ne va que
      // dans le sens de la prudence et ne rattraperait pas une promesse trop
      // forte sur un verbe qui produirait par accident une révision.
      reviewability: 'direct_untracked',
      description: `Note de bas de page ${input.note_id} réécrite en direct — l'avocat n'en verra aucune trace `
        + 'de révision.',
    }],
  });
}

/** La postcondition de réécriture : la note visée doit se relire avec le NOUVEAU texte. */
function controleDeReecriture(
  notesApres: readonly CanvasSnapshotFootnote[],
  noteId: string,
  texte: string,
): VerificationCheck {
  const relue = notesApres.find((note) => note.noteId === noteId);
  const tenu = relue !== undefined && correspondAuTexteDemande(relue.text, texte);
  return {
    name: 'note_relue_reecrite',
    passed: tenu,
    message: tenu
      ? `Relecture : la note ${noteId} porte bien le texte demandé.`
      : relue === undefined
        ? `edit_footnote a été annoncé en succès par l'éditeur, mais la note ${noteId} ne figure plus dans les `
          + `${notesApres.length} note(s) relue(s). Relis le document par canvas_inspect en incluant le domaine `
          + '"footnotes" avant toute autre action.'
        : `edit_footnote a été annoncé en succès par l'éditeur, mais la note ${noteId} se relit toujours `
          + `« ${extrait(relue.text ?? '')} » au lieu du texte demandé. La réécriture n'est pas prouvée. Relis le `
          + 'document par canvas_inspect en incluant le domaine "footnotes" avant toute autre action.',
  };
}

/**
 * Retirer une note et son renvoi (relevé 2026-08-26).
 *
 * ⚠️ **Sous régime suivi, la note RESTE listée par `footnotes.list()`**, avec
 * son contenu, marquée en attente de revue — elle ne disparaîtra qu'une fois la
 * révision acceptée par l'avocat. C'est mesuré, et c'est ce qui interdit toute
 * postcondition par COMPTAGE : « il y a toujours N notes » ne dit rien de la
 * suppression, et « il y en a N-1 » ne se produit qu'en régime direct.
 */
export async function executeDeleteFootnote(
  ctx: CanvasActionContext,
  input: DeleteFootnoteInput,
): Promise<CanvasActionReceipt> {
  const pre = await snapshot(ctx);

  if (typeof ctx.doc.footnotes?.remove !== 'function') {
    return refusDeCapacite(input.action, pre, 'footnotes.remove',
      'La note reste dans l\'acte. Dis à l\'avocat laquelle retirer plutôt que de vider son texte par edit_footnote : '
      + 'le renvoi resterait peint dans le corps, appelant une note vide.');
  }
  if (typeof ctx.doc.footnotes.list !== 'function') {
    return refusDeCapacite(input.action, pre, 'footnotes.list',
      'Sans elle, l\'identifiant visé ne peut être vérifié avant l\'écriture, et une suppression au mauvais '
      + 'identifiant retire une source que l\'avocat a choisie.');
  }

  // Même garde que pour `edit_footnote`, et elle pèse plus lourd ici : une
  // suppression au mauvais identifiant retire une source que l'avocat a choisie.
  if (!pre.footnotes.some((note) => note.noteId === input.note_id)) {
    return refusDeNoteInconnue(input.action, pre, input.note_id, pre.footnotes);
  }

  const revisionsAvant = idsDeRevision(pre.trackedChanges);
  const attendue = await revisionCourante(ctx, pre.revision);
  const resultat = await muterSousFences(
    ctx,
    'delete_footnote:footnotes.remove',
    { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    () => ctx.doc.footnotes?.remove?.(
      { target: adresseDeNote(input.note_id) },
      { changeMode: modeDeRevision(ctx), expectedRevision: attendue },
    ),
  );

  if (estUnRefus(resultat)) {
    return receiptDeRefusFournisseur({
      intent: input.action,
      resume: pre,
      resultat,
      sortie: 'La note reste dans l\'acte.',
      nextAction: 'Relis les notes par canvas_inspect avec includeDomains contenant "footnotes", puis reprends le '
        + 'noteId tel quel.',
      recovery: {
        action: 'reinspect',
        reason: 'Rien n\'a été écrit ; l\'état relu des notes dira si l\'identifiant visé existe encore.',
      },
    });
  }

  const post = await snapshot(ctx);
  ctx.verifierEpoque('delete_footnote:receipt');

  const revisionsApres = idsDeRevision(post.trackedChanges);
  const identitesNeuves = revisionsApres.filter((id) => !revisionsAvant.includes(id));

  return buildVerifiedReceipt({
    intent: input.action,
    regimeSuivi: ctx.regimeSuivi,
    preSnapshot: toCanvasSnapshotSummary(pre),
    postSnapshot: toCanvasSnapshotSummary(post),
    resolvedTargets: [],
    provider: resultat,
    revisionsAvant,
    revisionsApres,
    // `footnotes.remove` est mesuré produisant UNE révision sous fenêtre
    // `suggesting` ; le défaut (`apparition`) est donc juste, et le régime
    // direct est rabattu par `buildVerifiedReceipt`.
    verification: [controleDeSuppression(post.footnotes, input.note_id, identitesNeuves.length)],
    effects: [{
      kind: 'delete_footnote',
      reviewability: 'tracked',
      description: `Note de bas de page ${input.note_id} retirée, renvoi compris.`,
    }],
  });
}

/**
 * La postcondition de suppression — et le piège qu'elle refuse d'ignorer.
 *
 * ⚠️ **Ne JAMAIS écrire ici une vérification par comptage.** Mesuré le
 * 2026-08-26 : sous régime suivi, la note supprimée **reste listée** par
 * `footnotes.list()`, avec son contenu, en attente de revue. Une postcondition
 * du type « la liste doit avoir rétréci » rendrait donc `failed` sur une
 * suppression parfaitement réussie — c'est la jumelle exacte de la géométrie de
 * table qui ne rétrécit pas sous suivi (#994), et c'est le même remède : nommer
 * les DEUX preuves.
 *
 * Deux preuves, l'une ou l'autre suffit :
 * - la note a **disparu** de la relecture (régime direct) ;
 * - une **identité de révision neuve** est apparue (régime suivi) — la note est
 *   alors toujours là, et c'est normal.
 */
function controleDeSuppression(
  notesApres: readonly CanvasSnapshotFootnote[],
  noteId: string,
  identitesNeuves: number,
): VerificationCheck {
  const disparue = !notesApres.some((note) => note.noteId === noteId);
  const prouve = disparue || identitesNeuves > 0;
  return {
    name: 'note_retiree_ou_marquee',
    passed: prouve,
    message: prouve
      ? disparue
        ? `Relecture : la note ${noteId} ne figure plus dans footnotes.list.`
        : `Relecture : la note ${noteId} figure TOUJOURS dans footnotes.list, et c'est le comportement normal sous `
          + 'suivi des modifications — elle y reste jusqu\'à ce que l\'avocat accepte la révision. La suppression est '
          + `prouvée par ${identitesNeuves} identité(s) de révision neuve(s). Ne rejoue pas ce verbe.`
      : `delete_footnote a été annoncé en succès par l'éditeur, mais la note ${noteId} se relit toujours et AUCUNE `
        + 'révision neuve n\'est apparue : ni la disparition ni la mise en attente de revue ne sont établies. Relis le '
        + 'document par canvas_inspect en incluant le domaine "footnotes" avant toute autre action — ne rejoue pas ce '
        + 'verbe à l\'identique.',
  };
}

export const CANVAS_FOOTNOTE_ACTION_EXECUTORS = {
  insert_footnote: executeInsertFootnote,
  edit_footnote: executeEditFootnote,
  delete_footnote: executeDeleteFootnote,
} satisfies Record<NomDeVerbeDeNote, (ctx: CanvasActionContext, input: never) => Promise<CanvasActionReceipt>>;
